import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './tetris.css';
import LeaderboardNameModal from '../common/leaderboardNameModal';
import {
  getPlayerNameValidationMessage,
  getSavedPlayerName,
  submitGameHighscore,
} from '../../../services/leaderboardService';
import { TouchInputAdapter, triggerHapticFeedback } from '../common/touchInputAdapter';
import {
  BOARD_HEIGHT,
  BOARD_WIDTH,
  PIECES,
  TetrisAction,
  TetrisGameState,
  applyTetrisAction,
  checkCollision,
  createInitialTetrisState,
} from './tetrisEngine';

const audioFile = require('./tetris-theme.mp3');

interface TetrisGameProps {
  onClose?: () => void;
  onReload?: () => void;
  onLeaderboardUpdated?: () => void;
}

function TetrisGame({ onClose, onReload, onLeaderboardUpdated }: TetrisGameProps) {
  const { t } = useTranslation();
  // Level configurations with increasing difficulty
  const levelConfigs = useMemo(() => [
    { level: 1, fallSpeed: 800, linesRequired: 10, nameKey: 'tetrisGame.levelNames.1' },
    { level: 2, fallSpeed: 650, linesRequired: 20, nameKey: 'tetrisGame.levelNames.2' },
    { level: 3, fallSpeed: 500, linesRequired: 30, nameKey: 'tetrisGame.levelNames.3' },
    { level: 4, fallSpeed: 400, linesRequired: 40, nameKey: 'tetrisGame.levelNames.4' },
    { level: 5, fallSpeed: 320, linesRequired: 50, nameKey: 'tetrisGame.levelNames.5' },
    { level: 6, fallSpeed: 250, linesRequired: 60, nameKey: 'tetrisGame.levelNames.6' },
    { level: 7, fallSpeed: 200, linesRequired: 70, nameKey: 'tetrisGame.levelNames.7' },
    { level: 8, fallSpeed: 150, linesRequired: 80, nameKey: 'tetrisGame.levelNames.8' },
    { level: 9, fallSpeed: 120, linesRequired: 90, nameKey: 'tetrisGame.levelNames.9' },
    { level: 10, fallSpeed: 100, linesRequired: 100, nameKey: 'tetrisGame.levelNames.10' }
  ], []);

  const [gameState, setGameState] = useState<TetrisGameState>(() => createInitialTetrisState());

  const [isPlaying, setIsPlaying] = useState(false);
  const [showControls, setShowControls] = useState(false);
  const [showStartScreen, setShowStartScreen] = useState(true);
  const [highScore, setHighScore] = useState(() => {
    const saved = localStorage.getItem('tetris-highscore');
    return saved ? parseInt(saved) : 0;
  });
  const [showNameModal, setShowNameModal] = useState(false);
  const [scoreNameError, setScoreNameError] = useState<string | null>(null);
  const [isSubmittingScore, setIsSubmittingScore] = useState(false);
  const [nameDefault, setNameDefault] = useState(getSavedPlayerName());
  const [isTinyPhone, setIsTinyPhone] = useState(false);

  const audioRef = useRef(new Audio(audioFile));
  const gameLoopRef = useRef<NodeJS.Timeout>();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const leaderboardSubmittedRef = useRef(false);
  const touchAdapterRef = useRef(
    new TouchInputAdapter({
      swipeThreshold: 28,
      tapMaxMovement: 12,
      tapMaxDurationMs: 280,
      doubleTapWindowMs: 260,
      emitDragEvents: false,
    }),
  );
  const [canvasSize, setCanvasSize] = useState({ width: 400, height: 800 });

  // Responsive canvas sizing
  const getCanvasSize = useCallback(() => {
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const gameRatio = BOARD_WIDTH / BOARD_HEIGHT;
    
    // Calculate available space for the game (subtract header, controls, and padding)
    const headerHeight = 120; // Approximate header height
    const controlsHeight = 150; // Approximate controls height
    const padding = 40; // Total padding
    const availableHeight = viewportHeight - headerHeight - controlsHeight - padding;
    
    // On smaller screens, prioritize fitting the game vertically
    if (viewportHeight <= 800 || viewportWidth <= 768) {
      // Use 90% of available height
      const height = Math.min(availableHeight * 0.9, 600);
      const width = height * gameRatio;
      
      // Ensure width doesn't exceed 90% of viewport width
      if (width > viewportWidth * 0.9) {
        const adjustedWidth = viewportWidth * 0.9;
        return { width: adjustedWidth, height: adjustedWidth / gameRatio };
      }
      
      return { width, height };
    } else {
      // Desktop - use 50% of viewport width or 400px, whichever is smaller
      const width = Math.min(viewportWidth * 0.4, 450);
      const height = width / gameRatio;
      
      // Ensure it fits within available height
      if (height > availableHeight) {
        const adjustedHeight = availableHeight;
        return { width: adjustedHeight * gameRatio, height: adjustedHeight };
      }
      
      return { width, height };
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setCanvasSize(getCanvasSize());
      setIsTinyPhone(window.innerWidth <= 420);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [getCanvasSize]);

  // Audio setup
  useEffect(() => {
    const audio = audioRef.current;
    audio.loop = true;
    audio.volume = 0.3;
    return () => {
      audio.pause();
      audio.currentTime = 0;
    };
  }, []);

  useEffect(() => {
    if (isPlaying) {
      audioRef.current.play().catch(() => {});
    } else {
      audioRef.current.pause();
    }
  }, [isPlaying]);

  const dispatchGameAction = useCallback((action: TetrisAction) => {
    setGameState(prev => applyTetrisAction(prev, action));
  }, []);

  useEffect(() => {
    if (gameState.score > highScore) {
      setHighScore(gameState.score);
      localStorage.setItem('tetris-highscore', gameState.score.toString());
    }
  }, [gameState.score, highScore]);

  // Move piece
  const movePiece = useCallback((direction: 'left' | 'right' | 'down') => {
    dispatchGameAction({ type: 'MOVE', direction });
  }, [dispatchGameAction]);

  // Rotate current piece
  const rotatePieceAction = useCallback((clockwise = true) => {
    dispatchGameAction({ type: 'ROTATE', clockwise });
  }, [dispatchGameAction]);

  // Hard drop
  const hardDrop = useCallback(() => {
    dispatchGameAction({ type: 'HARD_DROP' });
  }, [dispatchGameAction]);

  // Hold piece
  const holdPiece = useCallback(() => {
    dispatchGameAction({ type: 'HOLD' });
  }, [dispatchGameAction]);

  // Game loop
  useEffect(() => {
    if (showStartScreen || gameState.isGameOver || gameState.isPaused) {
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
      }
      return;
    }
    
    const currentLevelConfig = levelConfigs[gameState.level - 1];
    const interval = currentLevelConfig ? currentLevelConfig.fallSpeed : 800;
    
    gameLoopRef.current = setInterval(() => {
      movePiece('down');
    }, interval);
    
    return () => {
      if (gameLoopRef.current) {
        clearInterval(gameLoopRef.current);
      }
    };
  }, [showStartScreen, gameState.isGameOver, gameState.isPaused, gameState.level, movePiece, levelConfigs]);

  // Initialize game
  const initializeGame = useCallback(() => {
    dispatchGameAction({ type: 'INIT' });
  }, [dispatchGameAction]);

  // Start new game
  const startNewGame = useCallback(() => {
    leaderboardSubmittedRef.current = false;
    setShowNameModal(false);
    setScoreNameError(null);
    initializeGame();
  }, [initializeGame]);

  const startFromIntro = useCallback(() => {
    setShowStartScreen(false);
    startNewGame();
  }, [startNewGame]);

  useEffect(() => {
    if (leaderboardSubmittedRef.current || !gameState.isGameOver || gameState.score <= 0) {
      return;
    }

    setNameDefault(getSavedPlayerName());
    setScoreNameError(null);
    setShowNameModal(true);
  }, [gameState.isGameOver, gameState.score, onLeaderboardUpdated]);

  const handleScoreSubmit = useCallback(async (name: string) => {
    const error = getPlayerNameValidationMessage(name);
    if (error) {
      setScoreNameError(error);
      return;
    }

    setIsSubmittingScore(true);
    setScoreNameError(null);
    try {
      const submitted = await submitGameHighscore('tetris', name, gameState.score);
      if (submitted) {
        leaderboardSubmittedRef.current = true;
        setShowNameModal(false);
        onLeaderboardUpdated?.();
      } else {
        setScoreNameError('leaderboardModal.errorOnlineUnavailable');
      }
    } catch (submitError) {
      setScoreNameError('leaderboardModal.errorSubmit');
      console.error('Tetris leaderboard submit failed:', submitError);
    } finally {
      setIsSubmittingScore(false);
    }
  }, [gameState.score, onLeaderboardUpdated]);

  const handleScoreSkip = useCallback(() => {
    leaderboardSubmittedRef.current = true;
    setScoreNameError(null);
    setShowNameModal(false);
  }, []);

  // Toggle pause
  const togglePause = useCallback(() => {
    dispatchGameAction({ type: 'TOGGLE_PAUSE' });
  }, [dispatchGameAction]);

  // Keyboard controls
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (showStartScreen || gameState.isGameOver) return;
      
      switch (event.key.toLowerCase()) {
        case 'arrowleft':
        case 'a':
          event.preventDefault();
          movePiece('left');
          break;
        case 'arrowright':
        case 'd':
          event.preventDefault();
          movePiece('right');
          break;
        case 'arrowdown':
        case 's':
          event.preventDefault();
          movePiece('down');
          break;
        case 'arrowup':
        case 'w':
          event.preventDefault();
          rotatePieceAction(true);
          break;
        case 'q':
          event.preventDefault();
          rotatePieceAction(false);
          break;
        case ' ':
          event.preventDefault();
          hardDrop();
          break;
        case 'c':
          event.preventDefault();
          holdPiece();
          break;
        case 'p':
          event.preventDefault();
          togglePause();
          break;
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [movePiece, rotatePieceAction, hardDrop, holdPiece, togglePause, gameState.isGameOver, showStartScreen]);

  const emitHaptic = useCallback((level: 'light' | 'medium') => {
    triggerHapticFeedback(level);
  }, []);

  const processTouchEvents = useCallback((events: ReturnType<TouchInputAdapter['move']>) => {
    for (const event of events) {
      switch (event.type) {
        case 'swipeLeft':
          movePiece('left');
          emitHaptic('light');
          break;
        case 'swipeRight':
          movePiece('right');
          emitHaptic('light');
          break;
        case 'swipeDown':
          movePiece('down');
          emitHaptic('light');
          break;
        case 'tap':
          rotatePieceAction(true);
          emitHaptic('light');
          break;
        case 'doubleTap':
          hardDrop();
          emitHaptic('medium');
          break;
      }
    }
  }, [emitHaptic, hardDrop, movePiece, rotatePieceAction]);

  // Touch-first controls:
  // tap = rotate, double tap = hard drop, horizontal swipe = move, down swipe = soft drop.
  const handleTouchStart = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    if (gameState.isGameOver || gameState.isPaused || !gameState.currentPiece) {
      return;
    }

    const touch = e.touches[0];
    touchAdapterRef.current.start(touch.clientX, touch.clientY, Date.now());
  }, [gameState.currentPiece, gameState.isGameOver, gameState.isPaused]);

  const handleTouchMove = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    if (gameState.isGameOver || gameState.isPaused || !gameState.currentPiece) {
      return;
    }

    e.preventDefault();

    const touch = e.touches[0];
    const events = touchAdapterRef.current.move(touch.clientX, touch.clientY, Date.now());
    processTouchEvents(events);
  }, [gameState.currentPiece, gameState.isGameOver, gameState.isPaused, processTouchEvents]);

  const handleTouchEnd = useCallback((e: React.TouchEvent<HTMLCanvasElement>) => {
    if (gameState.isGameOver || gameState.isPaused || !gameState.currentPiece) {
      return;
    }

    const touch = e.changedTouches[0];
    const events = touchAdapterRef.current.end(touch.clientX, touch.clientY, Date.now());
    processTouchEvents(events);
  }, [gameState.currentPiece, gameState.isGameOver, gameState.isPaused, processTouchEvents]);

  const handleTouchCancel = useCallback(() => {
    touchAdapterRef.current.cancel();
  }, []);

  // Initialize game on mount
  useEffect(() => {
    initializeGame();
  }, [initializeGame]);

  // Calculate ghost piece position
  const getGhostPiecePosition = useCallback(() => {
    if (!gameState.currentPiece) return null;
    
    let ghostY = gameState.currentPiece.position.y;
    
    // Move down until collision
    while (ghostY < BOARD_HEIGHT) {
      if (!checkCollision(gameState.currentPiece, gameState.board, 0, ghostY + 1 - gameState.currentPiece.position.y)) {
        ghostY++;
      } else {
        break;
      }
    }
    
    return { x: gameState.currentPiece.position.x, y: ghostY };
  }, [gameState.currentPiece, gameState.board]);

  // Render game board
  const renderBoard = useCallback(() => {
    if (!canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d')!;
    const cellWidth = canvasSize.width / BOARD_WIDTH;
    const cellHeight = canvasSize.height / BOARD_HEIGHT;
    
    // Clear canvas
    ctx.fillStyle = '#000012';
    ctx.fillRect(0, 0, canvasSize.width, canvasSize.height);
    
    // Draw board
    for (let y = 0; y < BOARD_HEIGHT; y++) {
      for (let x = 0; x < BOARD_WIDTH; x++) {
        const cell = gameState.board[y][x];
        if (cell) {
          ctx.fillStyle = cell;
          ctx.fillRect(x * cellWidth, y * cellHeight, cellWidth - 1, cellHeight - 1);
        }
        
        // Draw grid
        ctx.strokeStyle = '#222244';
        ctx.lineWidth = 0.5;
        ctx.strokeRect(x * cellWidth, y * cellHeight, cellWidth, cellHeight);
      }
    }
    
    // Draw ghost piece (outline where current piece will land)
    const ghostPosition = getGhostPiecePosition();
    if (gameState.currentPiece && ghostPosition && ghostPosition.y !== gameState.currentPiece.position.y) {
      ctx.strokeStyle = gameState.currentPiece.color;
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 4]); // Dashed outline
      
      for (let y = 0; y < gameState.currentPiece.shape.length; y++) {
        for (let x = 0; x < gameState.currentPiece.shape[y].length; x++) {
          if (gameState.currentPiece.shape[y][x]) {
            const boardX = ghostPosition.x + x;
            const boardY = ghostPosition.y + y;
            if (boardY >= 0) {
              ctx.strokeRect(
                boardX * cellWidth + 1,
                boardY * cellHeight + 1,
                cellWidth - 3,
                cellHeight - 3
              );
            }
          }
        }
      }
      ctx.setLineDash([]); // Reset line dash
    }
    
    // Draw current piece
    if (gameState.currentPiece) {
      ctx.fillStyle = gameState.currentPiece.color;
      for (let y = 0; y < gameState.currentPiece.shape.length; y++) {
        for (let x = 0; x < gameState.currentPiece.shape[y].length; x++) {
          if (gameState.currentPiece.shape[y][x]) {
            const boardX = gameState.currentPiece.position.x + x;
            const boardY = gameState.currentPiece.position.y + y;
            if (boardY >= 0) {
              ctx.fillRect(
                boardX * cellWidth,
                boardY * cellHeight,
                cellWidth - 1,
                cellHeight - 1
              );
            }
          }
        }
      }
    }
  }, [gameState.board, gameState.currentPiece, canvasSize, getGhostPiecePosition]);

  useEffect(() => {
    renderBoard();
  }, [renderBoard]);

  const currentLevelConfig = levelConfigs[gameState.level - 1];

  return (
    <div className="tetris-game-container">
      <div className="tetris-header">
        <div className="header-left">
          <button
            onClick={() => setIsPlaying(!isPlaying)}
            className="music-button"
          >
            {isPlaying ? '🔊' : '🔇'} {t('gamesCommon.music')}
          </button>
        </div>
        
        <div className="tetris-score-section">
          <div className="score-item">
            <span className="score-label">{t('gamesCommon.score')}</span>
            <span className="score-value">{gameState.score.toLocaleString()}</span>
          </div>
          <div className="score-item">
            <span className="score-label">{t('gamesCommon.highScore')}</span>
            <span className="score-value">{highScore.toLocaleString()}</span>
          </div>
          <div className="score-item">
            <span className="score-label">{t('gamesCommon.level')}</span>
            <span className="score-value">{gameState.level}</span>
          </div>
          <div className="score-item">
            <span className="score-label">{t('tetrisGame.lines')}</span>
            <span className="score-value">{gameState.lines}</span>
          </div>
        </div>
        
        <div className="header-right">
          {onClose && (
            <button onClick={onClose} className="close-button">
              ✕
            </button>
          )}
        </div>
      </div>

      {showStartScreen ? (
        <div className="tetris-start-screen">
          <div className="tetris-start-title">{t('gamesCarousel.games.tetris.title')}</div>
          <div className="tetris-start-content">
            <p>{t('tetrisGame.startControls')}</p>
            <p>{t('tetrisGame.startPause')}</p>
            <p>{t('tetrisGame.startObjective')}</p>
            <p>{t('tetrisGame.hintKeyboard')}</p>
            <p>{t('tetrisGame.hintTouch')}</p>
          </div>
          <button onClick={startFromIntro} className="start-btn">{t('gamesCommon.startGame')}</button>
        </div>
      ) : (
      <>
      <div className="tetris-game-area">
        <div className="tetris-side-panel">
          <div className="held-piece-section">
            <h3>{t('tetrisGame.hold')}</h3>
            <div className="piece-preview">
              {gameState.heldPiece && (
                <div className="mini-piece">
                  {PIECES[gameState.heldPiece as keyof typeof PIECES].shape.map((row, y) => (
                    <div key={y} className="mini-row">
                      {row.map((cell, x) => (
                        <div
                          key={x}
                          className="mini-cell"
                          style={{
                            backgroundColor: cell
                              ? PIECES[gameState.heldPiece as keyof typeof PIECES].color
                              : 'transparent'
                          }}
                        />
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="level-info">
            <h3>{t('gamesCommon.levelWithValue', { level: gameState.level })}</h3>
            <p>{currentLevelConfig?.nameKey ? t(currentLevelConfig.nameKey) : ''}</p>
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{
                  width: `${Math.min((gameState.lines % 10) * 10, 100)}%`
                }}
              />
            </div>
            <p>{t('tetrisGame.linesProgress', { current: gameState.lines % 10, total: 10 })}</p>
          </div>
        </div>

        <div className="tetris-main-game">
          <canvas
            ref={canvasRef}
            width={canvasSize.width}
            height={canvasSize.height}
            className="tetris-canvas"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchCancel}
          />
          
          {gameState.isPaused && (
            <div className="game-overlay">
              <div className="overlay-content">
                <h2>{t('gamesCommon.paused')}</h2>
                <button onClick={togglePause} className="game-button">{t('gamesCommon.resume')}</button>
              </div>
            </div>
          )}
          
          {gameState.isGameOver && (
            <div className="game-overlay">
              <div className="overlay-content">
                <h2>{t('gamesCommon.gameOver')}</h2>
                <p>{t('gamesCommon.finalScore', { score: gameState.score.toLocaleString() })}</p>
                <p>{t('gamesCommon.levelReached', { level: gameState.level })}</p>
                {gameState.score === highScore && <p className="new-record">{t('gamesCommon.newHighScore')}</p>}
                <div className="overlay-buttons">
                  <button onClick={startNewGame} className="game-button">{t('gamesCommon.newGame')}</button>
                  {onReload && <button onClick={onReload} className="game-button">{t('gamesCommon.restart')}</button>}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="tetris-side-panel">
          <div className="next-pieces-section">
            <h3>{t('tetrisGame.next')}</h3>
            <div className="next-pieces">
              {gameState.nextPieces.slice(0, 3).map((pieceType, index) => (
                <div key={index} className="piece-preview">
                  <div className="mini-piece">
                    {PIECES[pieceType as keyof typeof PIECES].shape.map((row, y) => (
                      <div key={y} className="mini-row">
                        {row.map((cell, x) => (
                          <div
                            key={x}
                            className="mini-cell"
                            style={{
                              backgroundColor: cell
                                ? PIECES[pieceType as keyof typeof PIECES].color
                                : 'transparent'
                            }}
                          />
                        ))}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="tetris-controls">
        <div className="control-buttons">
          <button
            onClick={togglePause}
            className="control-button"
            disabled={gameState.isGameOver}
          >
            {gameState.isPaused ? '▶️' : '⏸️'}
          </button>
          <button
            onClick={() => setShowControls(!showControls)}
            className="control-button"
          >
            📱
          </button>
          <button onClick={startNewGame} className="control-button">
            🔄
          </button>
        </div>

        {showControls && (
          <div className={`mobile-controls ${isTinyPhone ? 'one-thumb' : ''}`}>
            {isTinyPhone ? (
              <div className="one-thumb-grid">
                <button onClick={() => rotatePieceAction(true)} className="mobile-button">↻</button>
                <button onClick={() => movePiece('left')} className="mobile-button">←</button>
                <button onClick={() => movePiece('right')} className="mobile-button">→</button>
                <button onClick={() => movePiece('down')} className="mobile-button">↓</button>
                <button onClick={hardDrop} className="mobile-button">⤓</button>
                <button onClick={holdPiece} className="mobile-button">{t('tetrisGame.hold')}</button>
              </div>
            ) : (
              <>
                <div className="mobile-control-row">
                  <button onClick={() => rotatePieceAction(false)} className="mobile-button">↺</button>
                  <button onClick={() => rotatePieceAction(true)} className="mobile-button">↻</button>
                  <button onClick={holdPiece} className="mobile-button">{t('tetrisGame.hold')}</button>
                </div>
                <div className="mobile-control-row">
                  <button onClick={() => movePiece('left')} className="mobile-button">←</button>
                  <button onClick={() => movePiece('down')} className="mobile-button">↓</button>
                  <button onClick={() => movePiece('right')} className="mobile-button">→</button>
                </div>
                <div className="mobile-control-row">
                  <button onClick={hardDrop} className="mobile-button wide">{t('tetrisGame.hardDrop')}</button>
                </div>
              </>
            )}
          </div>
        )}

        <div className="control-hints">
          <p>{t('tetrisGame.hintTouch')}</p>
          <p>{t('tetrisGame.hintKeyboard')}</p>
        </div>
      </div>
      </>
      )}
      <LeaderboardNameModal
        open={showNameModal}
        score={gameState.score}
        defaultName={nameDefault}
        isSubmitting={isSubmittingScore}
        error={scoreNameError}
        onSubmit={handleScoreSubmit}
        onCancel={handleScoreSkip}
      />
    </div>
  );
}

export default TetrisGame;